import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Container, Spinner, EmptyState } from "@/components/common";
import { LevelBadge } from "@/components/LevelBadge";
import { PersonalControls } from "@/components/PersonalControls";
import { LikeButton } from "@/components/LikeButton";
import { YouTubeEmbed } from "@/components/YouTubeEmbed";
import { fileUrl } from "@/components/FileUpload";
import { formatDate } from "@/lib/format";
import { FileText, ExternalLink, Music2, Lock } from "lucide-react";

function Meta({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#6E5B51]">{label}</p>
      <p className="mt-0.5 font-semibold text-[#2D1E18]">{value}</p>
    </div>
  );
}

function DanceTeaser({ d }) {
  const from = `/dances/${d.slug}`;
  return (
    <div data-testid="dance-teaser">
      <div className="border-b border-border bg-gradient-to-b from-[#F4EFE6] to-[#FAF7F2]">
        <Container className="py-10">
          <Link to="/dances" className="text-sm font-semibold text-[#B04B26] hover:underline">← Dance Library</Link>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <h1 className="font-display text-3xl font-extrabold text-[#2D1E18] sm:text-4xl">{d.name}</h1>
            <LevelBadge level={d.level} />
            <Lock size={22} className="text-[#B04B26]" />
          </div>
        </Container>
      </div>
      <Container className="py-12">
        <div className="mx-auto max-w-2xl rounded-3xl border-2 border-[#C85A32]/30 bg-[#FBEBE4]/60 p-8 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#C85A32] text-white"><Lock size={26} /></div>
          <h2 className="mt-4 font-display text-2xl font-extrabold text-[#2D1E18]">Unlock the full Dance Library!</h2>
          <p className="mt-2 text-[#5C4A42]">Create your free LVLDC account to access dance tutorials, step sheets, playlists and your own personal dance collection.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/register" state={{ from }} data-testid="teaser-register-btn" className="rounded-full bg-[#C85A32] px-6 py-3 font-bold text-white hover:bg-[#B04B26]">Create Free Account</Link>
            <Link to="/login" state={{ from }} data-testid="teaser-login-btn" className="rounded-full border border-[#C85A32] px-6 py-3 font-bold text-[#B04B26] hover:bg-[#C85A32] hover:text-white">Already a Member? Log In</Link>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default function DanceDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { data: d, isLoading, isError } = useQuery({ queryKey: ["dance", slug, !!user], queryFn: async () => (await api.get(`/dances/${slug}`)).data });
  const [statuses, setStatuses] = useState([]);
  useEffect(() => { if (d) setStatuses(d.my_statuses || []); }, [d]);

  if (isLoading) return <Spinner />;
  if (isError || !d) return <Container className="py-20"><EmptyState title="Dance not found" /></Container>;
  if (d.locked) return <DanceTeaser d={d} />;

  const extUrl = d.external_step_sheet_url || d.step_sheet_url || "";
  const stepPdf = d.step_sheet_path ? fileUrl(d.step_sheet_path) : (extUrl.toLowerCase().endsWith(".pdf") ? extUrl : "");

  return (
    <div>
      <div className="border-b border-border bg-gradient-to-b from-[#F4EFE6] to-[#FAF7F2]">
        <Container className="py-10">
          <Link to="/dances" className="text-sm font-semibold text-[#B04B26] hover:underline">← Dance Library</Link>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <h1 className="font-display text-3xl font-extrabold text-[#2D1E18] sm:text-4xl">{d.name}</h1>
            <LevelBadge level={d.level} />
          </div>
          {d.choreographer && <p className="mt-1 text-[#5C4A42]">Choreographed by {d.choreographer}</p>}
          <div className="mt-5 flex flex-wrap items-center gap-3"><PersonalControls dance={d} statuses={statuses} onChange={setStatuses} /><LikeButton dance={d} /></div>
        </Container>
      </div>

      <Container className="grid grid-cols-1 gap-8 py-10 lg:grid-cols-3">
        <div className="space-y-8 lg:col-span-2">
          {d.description && <p className="text-lg leading-relaxed text-[#5C4A42]">{d.description}</p>}
          {d.tutorial_url && (<div><h2 className="mb-3 font-display text-xl font-bold text-[#2D1E18]">Tutorial</h2><YouTubeEmbed url={d.tutorial_url} title={`${d.name} tutorial`} /></div>)}
          {d.demo_url && (<div><h2 className="mb-3 font-display text-xl font-bold text-[#2D1E18]">Demonstration</h2><YouTubeEmbed url={d.demo_url} title={`${d.name} demo`} /></div>)}
          {(d.extra_video_urls || []).filter(Boolean).map((u, i) => (
            <div key={i}><h2 className="mb-3 font-display text-xl font-bold text-[#2D1E18]">More video</h2><YouTubeEmbed url={u} title={`${d.name} video ${i + 1}`} /></div>
          ))}

          {(stepPdf || d.step_sheet_url || d.copperknob_url || d.external_step_sheet_url) && (
            <div>
              <h2 className="mb-3 font-display text-xl font-bold text-[#2D1E18]">Step sheet</h2>
              <div className="flex flex-wrap gap-3">
                {stepPdf && <a href={stepPdf} target="_blank" rel="noopener noreferrer" data-testid="stepsheet-pdf" className="inline-flex items-center gap-2 rounded-full bg-[#C85A32] px-5 py-3 font-semibold text-white hover:bg-[#B04B26]"><FileText size={18} /> Open full screen</a>}
                {(d.step_sheet_url || d.external_step_sheet_url) && !stepPdf && <a href={d.step_sheet_url || d.external_step_sheet_url} target="_blank" rel="noopener noreferrer" data-testid="stepsheet-link" className="inline-flex items-center gap-2 rounded-full bg-[#C85A32] px-5 py-3 font-semibold text-white hover:bg-[#B04B26]"><ExternalLink size={18} /> View step sheet</a>}
                {d.copperknob_url && <a href={d.copperknob_url} target="_blank" rel="noopener noreferrer" data-testid="copperknob-link" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 font-semibold text-[#5C4A42] hover:border-[#C85A32]"><ExternalLink size={18} /> CopperKnob</a>}
              </div>
              {stepPdf && (
                <div className="mt-4 overflow-hidden rounded-xl border border-border bg-white" data-testid="stepsheet-viewer">
                  <iframe title={`${d.name} step sheet`} src={stepPdf} className="w-full" style={{ height: 760 }} />
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <Meta label="Counts" value={d.counts} />
            <Meta label="Walls" value={d.walls} />
          </div>
          {(d.music || d.artist) && (
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#6E5B51]"><Music2 size={14} /> Music</p>
              <p className="mt-1 font-semibold text-[#2D1E18]">{d.music}</p>
              {d.artist && <p className="text-sm text-[#5C4A42]">{d.artist}</p>}
            </div>
          )}
          {d.tags?.length > 0 && <div className="flex flex-wrap gap-2">{d.tags.map((t) => <span key={t} className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-[#5C4A42]">#{t}</span>)}</div>}

          {d.recently_at?.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-display font-bold text-[#2D1E18]">Recently at our classes</h3>
              <ul className="mt-3 space-y-2">
                {d.recently_at.map((r, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 text-sm">
                    <Link to={`/classes/${r.class_slug}`} className="font-semibold text-[#B04B26] hover:underline">{r.class_name}</Link>
                    <span className="text-[#6E5B51]">{formatDate(r.date, { weekday: "short", year: true })}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </Container>
    </div>
  );
}
